import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, Phone, Mail, Clock, Heart, Star, Send, Check } from 'lucide-react';
import api from '../api/api';

const Footer = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [message, setMessage] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    try {
      const res = await api.post('/newsletter/subscribe', { email: email.trim(), first_name: name.trim() || undefined });
      setMessage(res.data.message);
      setStatus('success');
      setEmail('');
      setName('');
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Errore durante l\'iscrizione.');
      setStatus('error');
    }
  };

  const serviceLinks = [
    { to: '/servizi',               label: t('nav.services') },
    { to: '/shop',                  label: t('nav.shop') },
    { to: '/giardino-delle-stelle', label: t('nav.garden') },
    { to: '/comunita',              label: t('nav.community') },
  ];

  const companyLinks = [
    { to: '/chi-siamo',  label: t('nav.about') },
    { to: '/contatti',   label: t('nav.contact') },
    { to: '/login',      label: t('nav.login') },
    { to: '/registrati', label: t('nav.register') },
  ];

  return (
    <footer className="bg-[var(--color-deep)] text-[var(--color-linen)]">

      {/* ── Newsletter strip ── */}
      <div className="border-b border-white/10 py-10" style={{ background: 'linear-gradient(160deg, #0a1a10 0%, #1B3D2A 100%)' }}>
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="max-w-sm">
              <div className="flex items-center gap-2 text-amber-400 text-xs font-bold uppercase tracking-widest mb-3">
                <Star size={12} fill="currentColor" className="twinkle" /> Notizie dal Paradiso
              </div>
              <h3 className="font-serif text-xl text-white mb-2">La nostra newsletter mensile</h3>
              <p className="text-sm text-white/55 leading-relaxed">
                Ogni mese aggiornamenti dalla community, articoli sul lutto e cura, 
                e notizie dal Giardino delle Stelle — sempre con rispetto e affetto.
              </p>
            </div>

            <div className="w-full lg:w-auto lg:min-w-[440px]">
              {status === 'success' ? (
                <div className="flex items-center gap-3 bg-[var(--color-sage)]/20 border border-[var(--color-sage)]/30 rounded-2xl px-6 py-4" data-testid="newsletter-success">
                  <Check size={20} className="text-green-400 shrink-0" />
                  <p className="text-sm text-white/80">{message}</p>
                </div>
              ) : (
                <form onSubmit={handleSubscribe} className="space-y-3" data-testid="newsletter-form">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Il tuo nome (opzionale)"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      style={{ background: 'rgba(255,255,255,0.08)' }}
                      className="flex-1 border border-white/20 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[var(--color-sage)] transition-colors"
                      data-testid="newsletter-name-input"
                    />
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      placeholder="La tua email *"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      style={{ background: 'rgba(255,255,255,0.08)' }}
                      className="flex-1 border border-white/20 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[var(--color-sage)] transition-colors"
                      data-testid="newsletter-email-input"
                    />
                    <button
                      type="submit"
                      disabled={status === 'loading'}
                      className="bg-[var(--color-terracotta)] hover:bg-[var(--color-terracotta-dark)] text-white rounded-xl px-5 py-2.5 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-60 shrink-0"
                      data-testid="newsletter-subscribe-btn"
                    >
                      {status === 'loading' ? (
                        <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      ) : (
                        <Send size={14} />
                      )}
                      Iscriviti
                    </button>
                  </div>
                  {status === 'error' && (
                    <p className="text-xs text-red-400">{message}</p>
                  )}
                  <p className="text-[10px] text-white/30">Nessuno spam. Puoi cancellare l'iscrizione in qualsiasi momento.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Main footer grid ── */}
      <div className="container-custom py-12">
        <div className="flex flex-col items-center mb-10 pb-10 border-b border-white/10">
          <img src="/logo.png" alt="Una Zampa in Paradiso" className="h-24 w-auto object-contain rounded-xl shadow-lg mb-4" />
          <div className="flex items-center gap-3 mt-1">
            <div className="h-px w-10 bg-[var(--color-terracotta)]/60" />
            <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--color-stone)]">Il tuo amico per sempre</p>
            <div className="h-px w-10 bg-[var(--color-terracotta)]/60" />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-10 items-start">
          {/* Descrizione */}
          <div className="col-span-2 md:col-span-1">
            <p className="text-sm text-[var(--color-stone)] leading-relaxed mb-3">
              Con cura, dignità e rispetto per ogni animale e la sua famiglia.
            </p>
            <div className="flex items-center gap-2 text-[var(--color-terracotta)] text-sm font-semibold tracking-widest uppercase">
              <Heart size={14} fill="currentColor" className="animate-pulse" />
              <span>Amici per Sempre</span>
            </div>
          </div>

          <div>
            <h4 className="font-brand text-sm text-[#F5F5DC] mb-3 tracking-wide">{t('footer.services')}</h4>
            <ul className="space-y-1.5">
              {serviceLinks.map(link => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm text-[var(--color-stone)] hover:text-[#F5F5DC] transition-colors duration-150">{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-brand text-sm text-[#F5F5DC] mb-3 tracking-wide">{t('footer.quick_links')}</h4>
            <ul className="space-y-1.5">
              {companyLinks.map(link => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm text-[var(--color-stone)] hover:text-[#F5F5DC] transition-colors duration-150">{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-brand text-sm text-[#F5F5DC] mb-3 tracking-wide">{t('footer.contact')}</h4>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <MapPin size={12} className="mt-0.5 flex-shrink-0 text-[var(--color-sage)]" />
                <span className="text-sm text-[var(--color-stone)]">Via Mangiacane, 11 — 12011 Borgo San Dalmazzo (CN)</span>
              </li>
              <li className="flex items-center gap-2">
                <Phone size={12} className="flex-shrink-0 text-[var(--color-sage)]" />
                <a href="tel:+393454645573" className="text-sm text-[var(--color-stone)] hover:text-[#F5F5DC] transition-colors">+39 345 464 5573</a>
              </li>
              <li className="flex items-center gap-2">
                <svg viewBox="0 0 24 24" className="w-3 h-3 fill-[#25D366] flex-shrink-0"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                <a href="https://wa.me/393454645573" target="_blank" rel="noopener noreferrer" className="text-sm text-[var(--color-stone)] hover:text-[#25D366] transition-colors">WhatsApp</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={12} className="flex-shrink-0 text-[var(--color-sage)]" />
                <a href="mailto:info@unazampainparadiso.com" className="text-sm text-[var(--color-stone)] hover:text-[#F5F5DC] transition-colors">info@unazampainparadiso.com</a>
              </li>
              <li className="flex items-center gap-2">
                <Clock size={12} className="flex-shrink-0 text-[var(--color-sage)]" />
                <span className="text-sm text-[var(--color-stone)]">H24 · 7 giorni su 7</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-10 pt-6 flex flex-col gap-4">
          {/* Riga 1: Copyright + legal links */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-center sm:text-left">
              <p className="text-xs text-[var(--color-stone)]">
                &copy; {new Date().getFullYear()} Una Zampa in Paradiso S.r.l.s. — {t('footer.rights')}.
              </p>
              <p className="text-xs text-[var(--color-stone)]/70 mt-0.5">
                P.IVA 04183140047 · Via Mangiacane, 11 · 12011 Borgo San Dalmazzo (CN)
              </p>
            </div>
            <div className="flex items-center gap-4 flex-wrap justify-center">
              <Link to="/privacy" className="text-xs text-[var(--color-stone)] hover:text-[#F5F5DC] transition-colors">Privacy Policy</Link>
              <Link to="/cookie-policy" className="text-xs text-[var(--color-stone)] hover:text-[#F5F5DC] transition-colors">Cookie Policy</Link>
              <Link to="/termini-servizio" className="text-xs text-[var(--color-stone)] hover:text-[#F5F5DC] transition-colors">Termini di Servizio</Link>
            </div>
          </div>

          {/* Riga 2: Powered by + Designed by */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 border-t border-white/5 pt-4">
            <a
              href="https://www.hostafun.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[var(--color-stone)]/60 hover:text-[#F5F5DC] transition-colors"
              data-testid="footer-powered-by"
            >
              Powered By <span className="font-semibold text-[var(--color-stone)]">Host@Fun</span>
            </a>
            <span className="hidden sm:block text-[var(--color-stone)]/30">·</span>
            <a
              href="https://www.navitech.shop"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[var(--color-stone)]/60 hover:text-[#F5F5DC] transition-colors"
              data-testid="footer-designed-by"
            >
              Designed By <span className="font-semibold text-[var(--color-stone)]">N@ Vi Tech</span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
